import { FC } from 'react'
import { NotificationItem } from '../../../../types/notificationType';
import { Box, Typography } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { NotificationBody, NotificationDate, NotificationMessage, NotificationTitle, SingleNotificationContainer } from './Notification.styles';
import { setNotificationToEdit } from '../../../../store/reducers/notificationReducer';
import { editNotificationVisibilityThunk, fetchNotificationListThunk } from '../../../../store/thunk/notificationThunk';
import { useAppDispatch } from '../../../../hooks/StoreHooks';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLocale } from '../../../../hooks/UseLocale';
import dayjs from 'dayjs';

interface SingleNotificationProps {
    notificationData: NotificationItem;
    currentOffset: number;
    currentStatus: string;
}

const SingleNotification: FC<SingleNotificationProps> = ({
    notificationData,
    currentOffset,
    currentStatus
}) => {
    const {
        payloadType,
        status,
        createdTimestamp,
        payload
    } = notificationData;

    const { t } = useTranslation();

    let payloadJSON: Record<string, any> | null = null;
    try {
        payloadJSON = payload ? JSON.parse(payload) : null;
    } catch (error) {
        payloadJSON = null;
    }
    const subtypeValue = typeof payloadJSON?.subtype === "string" ? payloadJSON.subtype : "";
    const subtypeFirstCharLower = subtypeValue
        ? (subtypeValue.substring(0, 1).toLowerCase() + subtypeValue.substring(1)) as String
        : "";
    const locale = useLocale();
    const dispatch = useAppDispatch()
    const handleClick = async () => {
        dispatch(setNotificationToEdit({
            ...notificationData,
            status: status === "Read" ? "Unread" : "Read"
        }));
        await dispatch(editNotificationVisibilityThunk());
        await dispatch(fetchNotificationListThunk({
            offset: currentOffset,
            limit: 10,
            status: currentStatus
        }));
    }

    const renderSwitch = (payloadTypeSwitch) => {
        switch (payloadTypeSwitch) {
            case "GenericMessage":
                return (
                    <NotificationBody>
                        <Typography>
                            {payloadJSON?.title && <NotificationTitle>{payloadJSON.title}</NotificationTitle>}
                            {payloadJSON?.message && <NotificationMessage>{payloadJSON.message}</NotificationMessage>}
                            <NotificationDate>{createdTimestamp}</NotificationDate>
                        </Typography>
                    </NotificationBody>
                )
            case "ActionModuleNoFulfillmentStateReminder": {
                const moduleType = typeof payloadJSON?.serviceModuleType === "string"
                    ? payloadJSON.serviceModuleType.toLowerCase()
                    : "";
                if (moduleType === "absence") {
                    const reasonValue = typeof payloadJSON?.absenceReason === "string"
                        ? payloadJSON.absenceReason
                        : "";
                    const reasonKey = reasonValue
                        ? reasonValue.substring(0, 1).toLowerCase() + reasonValue.substring(1)
                        : "";
                    const approvalValue = typeof payloadJSON?.newApproval === "string"
                        ? payloadJSON.newApproval
                        : "";
                    const approvalKey = approvalValue
                        ? approvalValue.substring(0, 1).toLowerCase() + approvalValue.substring(1)
                        : "";
                    return (
                        <NotificationBody>
                            <Typography>
                                <NotificationMessage>
                                    {t("notifications.absenceApprovalNeeded", {
                                        moduleName: payloadJSON?.serviceModuleName ?? "-",
                                        start: payloadJSON?.startTimestamp
                                            ? dayjs(payloadJSON.startTimestamp).format("DD.MM.YYYY")
                                            : "-",
                                        end: payloadJSON?.endTimestamp
                                            ? dayjs(payloadJSON.endTimestamp).format("DD.MM.YYYY")
                                            : "-",
                                        reason: reasonKey
                                            ? t(`serviceModulesPage.absencePage.reason.${reasonKey}`, {
                                                defaultValue: reasonValue,
                                            })
                                            : "-",
                                        status: approvalKey
                                            ? t(`serviceModulesPage.absencePage.status.${approvalKey}`, {
                                                defaultValue: approvalValue,
                                            })
                                            : "-",
                                    })}{" "}
                                    {payloadJSON?.serviceModuleId && (
                                        <Link to={`/${locale}/servicemodules/absence/${payloadJSON.serviceModuleId}/all`}>
                                            {t("notifications.absenceApprovalLink")}
                                        </Link>
                                    )}
                                </NotificationMessage>
                                <NotificationDate>
                                    {createdTimestamp
                                        ? createdTimestamp
                                        : t('notifications.noDateAvailable')}
                                </NotificationDate>
                            </Typography>
                        </NotificationBody>
                    )
                }
                return (
                    <NotificationBody>
                        <Typography>
                            {payloadJSON?.subtype && (
                                <NotificationMessage>
                                    {t(`notifications.actionModuleNoFulfillmentStateReminder`, {
                                        subtypename: t(`incentivationPage.${subtypeFirstCharLower}`)
                                    })}
                                    <Link to={`/${locale}/incentivation/taxfreebenefit/${subtypeFirstCharLower}`}>
                                        {t(`incentivationPage.${subtypeFirstCharLower}`)}
                                    </Link>
                                </NotificationMessage>
                            )}
                            <NotificationDate>
                                {createdTimestamp
                                    ? createdTimestamp
                                    : t('notifications.noDateAvailable')}
                            </NotificationDate>
                        </Typography>
                    </NotificationBody>
                )
            }
            case "ActionModuleNoScoreReminder":
                return (
                    <NotificationBody>
                        <Typography>
                            {payloadJSON?.subtype && <NotificationMessage>{t(`notifications.actionModuleNoScoreReminder`, { subtypename: t(`incentivationPage.${subtypeFirstCharLower}`) })} <Link to={window.location.origin + `/${locale}/incentivation/taxfreebenefit/${subtypeFirstCharLower}`}> {t(`incentivationPage.${subtypeFirstCharLower}`)}</Link></NotificationMessage>}
                            <NotificationDate>
                                {createdTimestamp
                                    ? createdTimestamp
                                    : t('notifications.noDateAvailable')}
                            </NotificationDate>
                        </Typography>
                    </NotificationBody>
                )
            case "ActionModuleTimeSheetApprovalNeeded":
                return (
                    <NotificationBody>
                        <Typography>
                            <NotificationMessage>
                                {t("notifications.timesheetApprovalNeeded", {
                                    moduleName: payloadJSON?.serviceModuleName ?? "-",
                                    start: payloadJSON?.startTimestamp
                                        ? dayjs(payloadJSON.startTimestamp).format("DD.MM.YYYY HH:mm")
                                        : "-",
                                    end: payloadJSON?.endTimestamp
                                        ? dayjs(payloadJSON.endTimestamp).format("DD.MM.YYYY HH:mm")
                                        : "-",
                                })}{" "}
                                {payloadJSON?.serviceModuleId && (
                                    <Link to={`/${locale}/servicemodules/timetracking/${payloadJSON.serviceModuleId}/all`}>
                                        {t("notifications.timesheetApprovalLink")}
                                    </Link>
                                )}
                            </NotificationMessage>
                            <NotificationDate>
                                {createdTimestamp
                                    ? createdTimestamp
                                    : t('notifications.noDateAvailable')}
                            </NotificationDate>
                        </Typography>
                    </NotificationBody>
                )
            case "ActionModuleAbsenceApprovalNeeded": {
                const reasonValue = typeof payloadJSON?.absenceReason === "string"
                    ? payloadJSON.absenceReason
                    : "";
                const reasonKey = reasonValue
                    ? reasonValue.substring(0, 1).toLowerCase() + reasonValue.substring(1)
                    : "";
                const approvalValue = typeof payloadJSON?.newApproval === "string"
                    ? payloadJSON.newApproval
                    : "";
                const approvalKey = approvalValue
                    ? approvalValue.substring(0, 1).toLowerCase() + approvalValue.substring(1)
                    : "";
                return (
                    <NotificationBody>
                        <Typography>
                            <NotificationMessage>
                                {t("notifications.absenceApprovalNeeded", {
                                    moduleName: payloadJSON?.serviceModuleName ?? "-",
                                    start: payloadJSON?.startTimestamp
                                        ? dayjs(payloadJSON.startTimestamp).format("DD.MM.YYYY")
                                        : "-",
                                    end: payloadJSON?.endTimestamp
                                        ? dayjs(payloadJSON.endTimestamp).format("DD.MM.YYYY")
                                        : "-",
                                    reason: reasonKey
                                        ? t(`serviceModulesPage.absencePage.reason.${reasonKey}`, {
                                            defaultValue: reasonValue,
                                        })
                                        : "-",
                                    status: approvalKey
                                        ? t(`serviceModulesPage.absencePage.status.${approvalKey}`, {
                                            defaultValue: approvalValue,
                                        })
                                        : "-",
                                })}{" "}
                                {payloadJSON?.serviceModuleId && (
                                    <Link to={`/${locale}/servicemodules/absence/${payloadJSON.serviceModuleId}/all`}>
                                        {t("notifications.absenceApprovalLink")}
                                    </Link>
                                )}
                            </NotificationMessage>
                            <NotificationDate>
                                {createdTimestamp
                                    ? createdTimestamp
                                    : t('notifications.noDateAvailable')}
                            </NotificationDate>
                        </Typography>
                    </NotificationBody>
                )
            }
            case "ActionModuleRewardDeclined": {
                return (
                    <NotificationBody>
                        <Typography>
                            <NotificationMessage>
                                {t("notifications.rewardDeclined", {
                                    moduleName: payloadJSON?.actionModuleName ?? "-",
                                    userName: payloadJSON?.decliningUserName ?? "-",
                                    start: payloadJSON?.startDate
                                        ? dayjs(payloadJSON.startDate).format("DD.MM.YYYY")
                                        : "-",
                                    end: payloadJSON?.endDate
                                        ? dayjs(payloadJSON.endDate).format("DD.MM.YYYY")
                                        : "-",
                                })}{" "}
                                {payloadJSON?.actionModuleId && (
                                    <Link to={`/${locale}/incentivation/actionModules/${payloadJSON.actionModuleId}`}>
                                        {t("notifications.incentivationLink")}
                                    </Link>
                                )}
                            </NotificationMessage>
                            <NotificationDate>
                                {createdTimestamp ?? t('notifications.noDateAvailable')}
                            </NotificationDate>
                        </Typography>
                    </NotificationBody>
                )
            }
            default:
                return (
                    <></>
                )
        }
    }

    return (
        <SingleNotificationContainer
            onClick={handleClick}
            read={status === "Read" ? true : false}
        >
            {renderSwitch(payloadType)}
            <Box>
                {status === "Read" ? <Visibility /> : <VisibilityOff />}
            </Box>
        </SingleNotificationContainer>

    )
}

export default SingleNotification
